import type { AdminConnectionView, Role, SessionView, TemporaryPasswordView, UserView } from '@hilo/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type CSSProperties, type FormEvent, useId, useState } from 'react';
import { Link } from 'react-router';
import { adminKeys, fetchAdminConnections } from '../../lib/admin';
import { errorText } from '../../lib/copy';
import { fetchUsers, keys } from '../../lib/queries';
import { useCurrentUser } from '../../lib/session';
import { createUser, resetUserPassword, ROLE_HELP, ROLE_LABEL, ROLE_ORDER, updateUser } from '../../lib/team';
import { hueOf, initialsOf } from '../../ui/Avatar';
import { ChannelMark } from '../../ui/ChannelMark';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { IconAlert, IconKey, IconPencil, IconUserPlus, IconUsers } from '../../ui/icons';
import { useSensitiveAction } from '../auth/ReauthDialog';
import { AdminShell } from '../connections/AdminShell';
import { actionErrorText, formatDateTime } from '../connections/ConnectionsPage';
import styles from './Team.module.css';

const byName = (a: UserView, b: UserView) => a.displayName.localeCompare(b.displayName, 'es');
const people = (count: number) => (count === 1 ? '1 persona' : `${count} personas`);

function Face({ user }: { user: UserView }) {
  return (
    <span className={styles.face} style={{ '--hue': hueOf(user.id) } as CSSProperties} aria-hidden="true">
      {initialsOf(user.displayName)}
    </span>
  );
}

/** Rol con su explicación: radios nativos (flechas y espacio funcionan solos). */
function RoleField({ value, onChange, disabled }: { value: Role; onChange: (role: Role) => void; disabled?: boolean }) {
  const name = useId();
  return (
    <fieldset className={styles.roles} disabled={disabled}>
      <legend className={styles.label}>Rol</legend>
      {[...ROLE_ORDER].reverse().map((role) => (
        <label key={role} className={styles.role}>
          <input type="radio" name={name} value={role} checked={value === role} onChange={() => onChange(role)} />
          <span className={styles.roleText}>
            <span className={styles.roleName}>{ROLE_LABEL[role]}</span>
            <span className={styles.roleHelp}>{ROLE_HELP[role]}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

function CreateDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (view: TemporaryPasswordView) => void }) {
  const client = useQueryClient();
  const { perform, dialog } = useSensitiveAction();
  const formId = useId();
  const ids = { email: useId(), name: useId() };
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('AGENT');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const view = await perform(() => createUser({ email: email.trim(), displayName: name.trim(), role }));
      if (view === undefined) return;
      await client.invalidateQueries({ queryKey: keys.users });
      onCreated(view);
    } catch (failure) {
      setError(actionErrorText(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <ConfirmDialog open variant="form" icon={<IconUserPlus />} title="Agregar persona al equipo"
        subtitle="Recibirá una contraseña temporal para su primer acceso."
        confirmLabel="Agregar persona" busyLabel="Agregando…" formId={formId}
        confirmDisabled={!email.trim() || !name.trim()} busy={busy} onConfirm={() => undefined} onCancel={onClose}>
        <form id={formId} className={styles.form} onSubmit={(event) => void submit(event)}>
          <label className="field" htmlFor={ids.name}>
            <span>Nombre</span>
            <input id={ids.name} className="input" value={name} maxLength={100} required autoFocus autoComplete="off"
              placeholder="Como se verá en la bandeja" disabled={busy} onChange={(event) => setName(event.target.value)} />
          </label>
          <label className="field" htmlFor={ids.email}>
            <span>Correo</span>
            <input id={ids.email} className="input" type="email" value={email} maxLength={254} required autoComplete="off"
              placeholder="nombre@empresa.com" disabled={busy} onChange={(event) => setEmail(event.target.value)} />
          </label>
          <RoleField value={role} onChange={setRole} disabled={busy} />
        </form>
        {error && <p className={styles.error} role="alert">{error}</p>}
      </ConfirmDialog>
      {dialog}
    </>
  );
}

function EditDialog({ user, self, onClose }: { user: UserView; self: boolean; onClose: () => void }) {
  const client = useQueryClient();
  const formId = useId();
  const nameId = useId();
  const [name, setName] = useState(user.displayName);
  const [role, setRole] = useState<Role>(user.role);
  const [active, setActive] = useState(user.active);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changes = {
    ...(name.trim() !== user.displayName ? { displayName: name.trim() } : {}),
    ...(role !== user.role ? { role } : {}),
    ...(active !== user.active ? { active } : {}),
  };
  const changed = Object.keys(changes).length > 0;
  // Cambiar el rol o activar/desactivar cierra la sesión de la persona (lo hace la API).
  const signsOut = 'role' in changes || 'active' in changes;
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!changed || !name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await updateUser(user.id, changes);
      // La sesión no se recarga sola (staleTime infinito): el menú debe ver el nombre nuevo al momento.
      if (self) client.setQueryData<SessionView | null>(keys.session, (session) => (session ? { ...session, user: updated } : session));
      await client.invalidateQueries({ queryKey: keys.users });
      onClose();
    } catch (failure) {
      setError(actionErrorText(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <ConfirmDialog open variant="form" icon={<IconPencil />} title={self ? 'Tu perfil' : `Editar a ${user.displayName}`} subtitle={user.email}
      confirmLabel="Guardar cambios" busyLabel="Guardando…" formId={formId}
      confirmDisabled={!changed || !name.trim()} busy={busy} onConfirm={() => undefined} onCancel={onClose}>
      <form id={formId} className={styles.form} onSubmit={(event) => void submit(event)}>
        <label className="field" htmlFor={nameId}>
          <span>Nombre</span>
          <input id={nameId} className="input" value={name} maxLength={100} required autoFocus autoComplete="off" disabled={busy}
            onChange={(event) => setName(event.target.value)} />
        </label>
        {self
          ? <p className={styles.hint}>Tu rol y el estado de tu cuenta los cambia otra persona de administración.</p>
          : (
            <>
              <RoleField value={role} onChange={setRole} disabled={busy} />
              <label className={styles.switch}>
                <input type="checkbox" role="switch" checked={active} disabled={busy} onChange={(event) => setActive(event.target.checked)} />
                <span className={styles.switchText}>
                  <span className={styles.roleName}>Cuenta activa</span>
                  <span className={styles.roleHelp}>Si la desactivas, no podrá entrar a Hilo. Puedes volver a activarla cuando quieras.</span>
                </span>
              </label>
            </>
          )}
      </form>
      {signsOut && (
        <p className={styles.note}>
          <IconAlert />
          <span>Al guardar se cerrará la sesión de {user.displayName} para aplicar el cambio.</span>
        </p>
      )}
      {error && <p className={styles.error} role="alert">{error}</p>}
    </ConfirmDialog>
  );
}

function ResetDialog({ user, onClose, onReset }: { user: UserView; onClose: () => void; onReset: (view: TemporaryPasswordView) => void }) {
  const client = useQueryClient();
  const { perform, dialog } = useSensitiveAction();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reset = async () => {
    setBusy(true);
    setError(null);
    try {
      const view = await perform(() => resetUserPassword(user.id));
      if (view === undefined) return;
      await client.invalidateQueries({ queryKey: keys.users });
      onReset(view);
    } catch (failure) {
      setError(actionErrorText(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <ConfirmDialog open icon={<IconKey />} title={`¿Restablecer la contraseña de ${user.displayName}?`}
        confirmLabel="Restablecer" busyLabel="Restableciendo…" busy={busy} onConfirm={() => void reset()} onCancel={onClose}>
        <p>Se cerrará su sesión y tendrá que entrar con una contraseña temporal nueva, que verás a continuación.</p>
        {error && <p className={styles.error} role="alert">{error}</p>}
      </ConfirmDialog>
      {dialog}
    </>
  );
}

/** La contraseña temporal se ve una sola vez: se copia aquí o se vuelve a restablecer. */
function IssuedDialog({ issued, created, timeZone, onClose }: {
  issued: TemporaryPasswordView; created: boolean; timeZone: string; onClose: () => void;
}) {
  const [copied, setCopied] = useState<'yes' | 'no' | null>(null);
  const copy = () => {
    try {
      if (!navigator.clipboard) throw new Error('Sin portapapeles');
      navigator.clipboard.writeText(issued.temporaryPassword).then(() => setCopied('yes'), () => setCopied('no'));
    } catch {
      setCopied('no');
    }
  };
  const name = issued.user.displayName;
  return (
    <ConfirmDialog open variant="form" hideCancel icon={<IconKey />}
      title={created ? `${name} ya está en el equipo` : `Contraseña nueva para ${name}`}
      subtitle={`${issued.user.email} · ${ROLE_LABEL[issued.user.role]}`}
      confirmLabel="Listo" onConfirm={onClose} onCancel={onClose}>
      <p>Compártele esta contraseña temporal por un medio seguro. Hilo no la vuelve a mostrar.</p>
      <div className={styles.secret}>
        {/* Campo de solo lectura: se selecciona con teclado o ratón si copiar no está disponible. */}
        <input className={styles.password} readOnly value={issued.temporaryPassword} aria-label="Contraseña temporal"
          spellCheck={false} autoComplete="off" onFocus={(event) => event.currentTarget.select()} />
        <button type="button" className={`btn btn-secondary ${styles.copy}`} onClick={copy}>
          {copied === 'yes' ? 'Copiada' : 'Copiar'}
        </button>
      </div>
      <p className={styles.hint} aria-live="polite">
        {copied === 'no'
          ? 'No se pudo copiar: selecciónala y cópiala a mano.'
          : `Vence el ${formatDateTime(issued.expiresAt, timeZone)}. Al entrar tendrá que elegir una contraseña propia.`}
      </p>
      {created && issued.user.role !== 'ADMIN' && (
        <p className={styles.next}>
          Siguiente paso: <Link to="/conexiones" onClick={onClose}>dale acceso a las cuentas que atenderá</Link>.
        </p>
      )}
    </ConfirmDialog>
  );
}

function Accounts({ user, connections }: { user: UserView; connections: AdminConnectionView[] | undefined }) {
  if (user.role === 'ADMIN') return <span className={styles.accountsText}>Ve todas las cuentas</span>;
  if (!connections) return null;
  const mine = connections.filter((connection) => connection.accessUserIds.includes(user.id));
  if (mine.length === 0) return <span className={styles.nothing}>Sin cuentas asignadas</span>;
  const names = mine.map((connection) => connection.displayName).join(', ');
  return (
    <span className={styles.accounts} title={names}>
      <span className={styles.marks} aria-hidden="true">
        {mine.slice(0, 4).map((connection) => <ChannelMark key={connection.id} channel={connection.channel} size="sm" />)}
      </span>
      <span className={styles.accountsText}>{mine.length === 1 ? '1 cuenta' : `${mine.length} cuentas`}</span>
      <span className="visually-hidden">: {names}</span>
    </span>
  );
}

function MemberRow({ user, self, showRole, connections, onEdit, onReset }: {
  user: UserView; self: boolean; showRole: boolean; connections: AdminConnectionView[] | undefined; onEdit: () => void; onReset: () => void;
}) {
  return (
    <li className={styles.row} data-inactive={!user.active || undefined}>
      <Face user={user} />
      <div className={styles.main}>
        <p className={styles.name}>
          <span>{user.displayName}</span>
          {self && <span className={styles.you}>Tú</span>}
          {showRole && <span className={styles.muted}>{ROLE_LABEL[user.role]}</span>}
        </p>
        <p className={styles.email}>{user.email}</p>
      </div>
      <div className={styles.side}>
        {!user.active
          ? <span className={styles.status} data-tone="off">Desactivada</span>
          : user.mustChangePassword
            ? <span className={styles.status} data-tone="warning">Contraseña temporal pendiente</span>
            : <Accounts user={user} connections={connections} />}
        <div className={styles.actions}>
          <button type="button" className={`btn btn-secondary ${styles.compact}`} onClick={onEdit}>
            <IconPencil />Editar<span className="visually-hidden"> a {user.displayName}</span>
          </button>
          {!self && user.role !== 'ADMIN' && (
            <button type="button" className={`btn btn-quiet ${styles.compact}`} onClick={onReset}>
              <IconKey />Restablecer contraseña<span className="visually-hidden"> de {user.displayName}</span>
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

/** Equipo (solo ADMIN): quién usa Hilo, con qué rol, qué cuentas atiende y su acceso. */
export function TeamPage() {
  const me = useCurrentUser();
  const users = useQuery({ queryKey: keys.users, queryFn: fetchUsers });
  const connections = useQuery({ queryKey: adminKeys.connections, queryFn: fetchAdminConnections });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<UserView | null>(null);
  const [resetting, setResetting] = useState<UserView | null>(null);
  const [issued, setIssued] = useState<{ view: TemporaryPasswordView; created: boolean } | null>(null);
  const all = users.data ?? [];
  const active = all.filter((user) => user.active);
  const inactive = all.filter((user) => !user.active).sort(byName);
  const pending = active.filter((user) => user.mustChangePassword).length;

  const row = (user: UserView, showRole: boolean) => (
    <MemberRow key={user.id} user={user} self={user.id === me.id} showRole={showRole} connections={connections.data}
      onEdit={() => setEditing(user)} onReset={() => setResetting(user)} />
  );

  return (
    <AdminShell>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Equipo</h1>
          <p className={styles.lead}>Las personas que usan Hilo, su rol y las cuentas que atienden.</p>
          {all.length > 0 && (
            <p className={styles.summary}>
              <span>{people(active.length)} con acceso</span>
              {pending > 0 && <span className={styles.summaryWarn}>{pending === 1 ? '1 aún no estrena su contraseña' : `${pending} aún no estrenan su contraseña`}</span>}
              {inactive.length > 0 && <span>{inactive.length === 1 ? '1 desactivada' : `${inactive.length} desactivadas`}</span>}
            </p>
          )}
        </div>
        <button type="button" className={`btn btn-primary ${styles.add}`} onClick={() => setCreating(true)}>
          <IconUserPlus />Agregar persona
        </button>
      </header>

      {users.isPending && <p className={styles.muted} role="status">Cargando equipo…</p>}
      {users.isError && <p className={styles.muted} role="alert">{errorText(users.error)}</p>}

      {ROLE_ORDER.map((role) => {
        const members = active.filter((user) => user.role === role).sort(byName);
        if (members.length === 0) return null;
        return (
          <section key={role} className={styles.section} aria-labelledby={`rol-${role}`}>
            <h2 id={`rol-${role}`} className={styles.groupTitle}>
              {ROLE_LABEL[role]}
              <span className={styles.count}>{people(members.length)}</span>
            </h2>
            <ul className={styles.panel}>{members.map((user) => row(user, false))}</ul>
          </section>
        );
      })}

      {inactive.length > 0 && (
        <section className={styles.section} aria-labelledby="rol-desactivadas">
          <h2 id="rol-desactivadas" className={styles.groupTitle}>
            Desactivadas
            <span className={styles.count}>{people(inactive.length)}</span>
          </h2>
          <ul className={styles.panel}>{inactive.map((user) => row(user, true))}</ul>
        </section>
      )}

      {users.isSuccess && all.length <= 1 && (
        <div className={styles.empty}>
          <span className={styles.emptyIcon} aria-hidden="true"><IconUsers /></span>
          <p>Por ahora solo estás tú. Agrega a las personas que atenderán las conversaciones.</p>
        </div>
      )}

      {creating && (
        <CreateDialog onClose={() => setCreating(false)}
          onCreated={(view) => { setCreating(false); setIssued({ view, created: true }); }} />
      )}
      {editing && <EditDialog user={editing} self={editing.id === me.id} onClose={() => setEditing(null)} />}
      {resetting && (
        <ResetDialog user={resetting} onClose={() => setResetting(null)}
          onReset={(view) => { setResetting(null); setIssued({ view, created: false }); }} />
      )}
      {issued && <IssuedDialog issued={issued.view} created={issued.created} timeZone={me.timezone} onClose={() => setIssued(null)} />}
    </AdminShell>
  );
}
